import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { todayLocalISO } from "../utils/date";
import { faCreditCard, faLayerGroup, faUserGroup, faRotateRight, faChartLine, faLock, faCheck, faXmark, faWallet, faHourglassHalf, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./PaymentsAdminModal.module.css";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";
import PlanEditorTable from "./PlanEditorTable";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const STATUS_LABELS = {
  pago: "Pago", pendente: "Pendente", cancelado: "Cancelado", sem_cobranca: "Sem cobrança",
};

// Mesmo mapa usado no backend (admin.py MRR / subscription_service.py
// INTERVAL_TO_ASAAS_CYCLE, ver R160 docs/07-issues.md) — meses por ciclo,
// pra calcular o equivalente mensal exibido nos cards de preço.
const MONTHS_PER_PLAN_INTERVAL = {
  weekly: 12 / 52, biweekly: 12 / 26, monthly: 1, bimonthly: 2,
  quarterly: 3, semiannual: 6, yearly: 12,
};
const PLAN_INTERVAL_LABELS = {
  weekly: "por semana", biweekly: "por quinzena", monthly: "por mês", bimonthly: "por bimestre",
  quarterly: "por trimestre", semiannual: "por semestre", yearly: "por ano",
};

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Erro inesperado.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(" - ");
  return JSON.stringify(detail);
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch (_) {
    return iso;
  }
}

function getShortName(fullName) {
  if (!fullName) return "-";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function UpgradeRequiredNotice({ onGoToPlans }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <FontAwesomeIcon icon={faLock} style={{ fontSize: "28px", color: "var(--text-0-50)", marginBottom: "12px" }} />
      <p style={{ color: "var(--text-primary)", fontWeight: "600", marginBottom: "6px" }}>
        Recurso do plano premium
      </p>
      <p style={{ color: "var(--text-0-50)", fontSize: "13px", marginBottom: "18px" }}>
        Seu plano atual não inclui a gestão de pagamentos dos alunos. Faça upgrade para liberar.
      </p>
      <button className={styles.planBtn} onClick={onGoToPlans}>
        Ver planos disponíveis
      </button>
    </div>
  );
}

function initialTab(hasActivePlatformSubscription, hasStudentBillingManagementAccess) {
  // Já paga a plataforma: abre direto no que interessa no dia a dia
  // (Financeiro), não na tela de escolher plano (já escolhido).
  if (hasActivePlatformSubscription) return "analytics";
  // Planos "aluno" (plan/students/analytics) exigem o plano premium do
  // professor (Plan.unlocks_student_billing) — sem isso, abre direto em
  // "Meu pagamento com a plataforma" pra guiar o upgrade.
  return hasStudentBillingManagementAccess ? "plan" : "myPayments";
}

export default function ProfessorBillingModal({
  open, onClose, currentUser, refetchBillingAccess, onOpenCheckout,
  hasStudentBillingManagementAccess = true, hasActivePlatformSubscription = false,
  lastSubscription = null, lastOneOffAccess = null,
}) {
  useBodyScrollLock(open);

  const [tab, setTab] = useState(initialTab(hasActivePlatformSubscription, hasStudentBillingManagementAccess));
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState("");
  const [searchStudent, setSearchStudent] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const STUDENTS_PER_PAGE = 10;
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Split de pagamento — subconta Asaas própria do professor (opção 2, ver
  // docs/01-produto.md). asaasSubaccount = null enquanto carrega; depois
  // { status: null|"pending"|"approved"|"rejected", has_wallet: bool }.
  const [asaasSubaccount, setAsaasSubaccount] = useState(null);
  const [loadingAsaas, setLoadingAsaas] = useState(false);
  const [asaasForm, setAsaasForm] = useState({
    cpf_cnpj: "", company_type: "MEI", birth_date: "", phone: "", mobile_phone: "",
    address: "", address_number: "", complement: "", province: "", postal_code: "", income_value: "",
  });
  const [submittingAsaas, setSubmittingAsaas] = useState(false);
  const [asaasError, setAsaasError] = useState("");
  const [cepStatus, setCepStatus] = useState("idle");

  const loadStudents = useCallback(async () => {
    setLoadingStudents(true);
    setError("");
    try {
      const { data } = await api.get("/payments/professor/students-status");
      setStudents(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  const filteredStudents = students.filter(s =>
    s.student_name.toLowerCase().includes(searchStudent.toLowerCase())
  );

  const totalPages = Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE);
  const startIdx = (currentPage - 1) * STUDENTS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(startIdx, startIdx + STUDENTS_PER_PAGE);

  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    setError("");
    try {
      const { data } = await api.get("/payments/professor/analytics");
      setAnalytics(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    setError("");
    try {
      const { data } = await api.get("/payments/plans");
      setPlans(data);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const loadAsaasSubaccount = useCallback(async () => {
    setLoadingAsaas(true);
    setAsaasError("");
    try {
      const { data } = await api.get("/payments/professor/asaas-subaccount");
      setAsaasSubaccount(data);
    } catch (err) {
      setAsaasError(extractError(err));
    } finally {
      setLoadingAsaas(false);
    }
  }, []);

  const handleAsaasFormChange = useCallback((field, value) => {
    setAsaasForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Busca de CEP via ViaCEP (API pública, sem chave) — ao completar 8 dígitos:
  // se encontrar, preenche endereço/bairro automaticamente e trava esses
  // campos como somente leitura (menos digitação, menos erro de digitação
  // divergindo do que a Receita/Correios têm cadastrado); se não encontrar
  // (CEP inexistente) ou a busca falhar (rede fora, ViaCEP indisponível),
  // libera os campos pro professor preencher na mão — nunca bloqueia o
  // cadastro por causa disso.
  const handleCepChange = useCallback((value) => {
    setAsaasForm(prev => ({ ...prev, postal_code: value }));
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 8) {
      setCepStatus("idle");
      return;
    }
    setCepStatus("loading");
    fetch(`https://viacep.com.br/ws/${digits}/json/`)
      .then(res => res.json())
      .then(data => {
        if (data?.erro) {
          setCepStatus("not_found");
          return;
        }
        setAsaasForm(prev => ({
          ...prev,
          address: data.logradouro || prev.address,
          province: data.bairro || prev.province,
        }));
        setCepStatus("found");
      })
      .catch(() => setCepStatus("error"));
  }, []);

  const handleSubmitAsaasSubaccount = useCallback(async (e) => {
    e.preventDefault();
    setSubmittingAsaas(true);
    setAsaasError("");
    try {
      const payload = { ...asaasForm };
      if (!payload.complement) delete payload.complement;
      payload.income_value = payload.income_value ? parseFloat(payload.income_value) : null;
      if (!payload.income_value) delete payload.income_value;
      const { data } = await api.post("/payments/professor/asaas-subaccount", payload);
      setAsaasSubaccount(data);
    } catch (err) {
      setAsaasError(extractError(err));
    } finally {
      setSubmittingAsaas(false);
    }
  }, [asaasForm]);

  const handleOpenCheckout = useCallback((plan) => {
    onOpenCheckout?.(plan);
  }, [onOpenCheckout]);

  useEffect(() => {
    if (!open) {
      setTab(initialTab(hasActivePlatformSubscription, hasStudentBillingManagementAccess));
      setError("");
      return;
    }
    if (tab === "students" && hasStudentBillingManagementAccess) loadStudents();
    // Já tem assinatura/avulso ativo: a aba mostra um resumo do que já foi
    // assinado (via lastSubscription/lastOneOffAccess, recebidos por prop) —
    // não precisa da lista de planos pra assinar de novo.
    if (tab === "myPayments" && !hasActivePlatformSubscription) loadPlans();
    if (tab === "analytics" && hasStudentBillingManagementAccess) loadAnalytics();
    if (tab === "receiving" && hasStudentBillingManagementAccess) loadAsaasSubaccount();
  }, [open, tab, hasStudentBillingManagementAccess, hasActivePlatformSubscription, loadStudents, loadPlans, loadAnalytics, loadAsaasSubaccount]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} style={{ maxWidth: 980 }} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faCreditCard} /></span>
            <div>
              <h2 className={styles.title}>Gestão de pagamentos</h2>
              <p className={styles.sub}>Gerencie seu pagamentos e dos seus alunos</p>
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <button className={`${styles.tabBtn} ${tab === "plan" ? styles.tabBtnActive : ""}`} onClick={() => setTab("plan")}>
            <FontAwesomeIcon icon={faLayerGroup} /> Planos de assinatura
          </button>
          <button className={`${styles.tabBtn} ${tab === "students" ? styles.tabBtnActive : ""}`} onClick={() => setTab("students")}>
            <FontAwesomeIcon icon={faUserGroup} /> Assinatura alunos
          </button>
          <button className={`${styles.tabBtn} ${tab === "analytics" ? styles.tabBtnActive : ""}`} onClick={() => setTab("analytics")}>
            <FontAwesomeIcon icon={faChartLine} /> Financeiro
          </button>
          {hasStudentBillingManagementAccess && (
            <button className={`${styles.tabBtn} ${tab === "receiving" ? styles.tabBtnActive : ""}`} onClick={() => setTab("receiving")}>
              <FontAwesomeIcon icon={faWallet} /> Recebimento direto
            </button>
          )}
          {currentUser?.is_self_service && (
            <button className={`${styles.tabBtn} ${tab === "myPayments" ? styles.tabBtnActive : ""}`} onClick={() => setTab("myPayments")}>
              <FontAwesomeIcon icon={faCreditCard} /> Meu pagamento com a plataforma
            </button>
          )}
        </div>

        <div className={styles.body}>
          {error && <div className={styles.error}>{error}</div>}

          {tab === "plan" ? (
            !hasStudentBillingManagementAccess ? (
              <UpgradeRequiredNotice onGoToPlans={() => setTab("myPayments")} />
            ) : (
              <>
                <p className={styles.sub} style={{ marginBottom: 12 }}>
                  Crie um plano de mensalidade - seus alunos vão vê-lo direto no menu
                  "Pagamentos" deles, com Pix ou Cartão de Crédito.
                </p>
                <PlanEditorTable
                  listUrl="/payments/professor/plans"
                  createUrl="/payments/professor/plans"
                  updateUrlPrefix="/payments/professor/plans"
                  showAudienceField={false}
                />
              </>
            )
          ) : tab === "students" ? (
            !hasStudentBillingManagementAccess ? (
              <UpgradeRequiredNotice onGoToPlans={() => setTab("myPayments")} />
            ) : (
            <div className={styles.listSection}>
              <div className={styles.miniStatRow}>
                <div className={styles.miniStatCard}>
                  <div className={styles.miniStatVal}>{students.filter(s => s.status === "pago").length}</div>
                  <div className={styles.miniStatLabel}>Pagos</div>
                </div>
                <div className={styles.miniStatCard}>
                  <div className={styles.miniStatVal}>{students.filter(s => s.status === "pendente").length}</div>
                  <div className={styles.miniStatLabel}>Pendentes</div>
                </div>
                <div className={styles.miniStatCard}>
                  <div className={styles.miniStatVal}>{students.filter(s => s.status === "cancelado").length}</div>
                  <div className={styles.miniStatLabel}>Cancelados</div>
                </div>
                <div className={styles.miniStatCard}>
                  <div className={styles.miniStatVal}>{students.filter(s => s.status === "sem_cobranca").length}</div>
                  <div className={styles.miniStatLabel}>Sem cobrança</div>
                </div>
              </div>
              <div className={styles.filterRow}>
                <input
                  type="text"
                  placeholder="Buscar aluno..."
                  value={searchStudent}
                  onChange={(e) => {
                    setSearchStudent(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "6px",
                    background: "var(--border-0-05)",
                    color: "var(--text-primary)",
                    fontSize: "13px"
                  }}
                />
                <button className={styles.iconBtn} onClick={loadStudents} title="Atualizar" aria-label="Atualizar lista de alunos">
                  <FontAwesomeIcon icon={faRotateRight} />
                </button>
              </div>
              {loadingStudents ? (
                <div className={styles.loadingMsg}>Carregando…</div>
              ) : (
                <>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Aluno</th><th>Status</th><th>Detalhe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStudents.map(s => (
                      <tr key={s.student_id}>
                        <td>{getShortName(s.student_name)}</td>
                        <td><span className={styles.statusPill}>{STATUS_LABELS[s.status] || s.status}</span></td>
                        <td className={styles.muted}>
                          {s.detail?.kind === "subscription" && s.detail?.current_period_end
                            ? `Próx. cobrança: ${formatDate(s.detail.current_period_end)}`
                            : s.detail?.kind === "one_off" && s.detail?.expires_at
                              ? `Válido até: ${formatDate(s.detail.expires_at)}`
                              : "-"}
                        </td>
                      </tr>
                    ))}
                      {paginatedStudents.length === 0 && filteredStudents.length === 0 && (
                        <tr><td colSpan={3} className={styles.emptyMsg}>Nenhum aluno vinculado.</td></tr>
                      )}
                      {paginatedStudents.length === 0 && filteredStudents.length > 0 && (
                        <tr><td colSpan={3} className={styles.emptyMsg}>Nenhum resultado encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                  {filteredStudents.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px", fontSize: "13px", color: "var(--text-0-50)" }}>
                      <span>{startIdx + 1}-{Math.min(startIdx + STUDENTS_PER_PAGE, filteredStudents.length)} de {filteredStudents.length}</span>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: "4px 12px",
                            border: "1px solid rgba(255,255,255,0.2)",
                            background: "transparent",
                            color: "var(--text-primary)",
                            borderRadius: "4px",
                            cursor: currentPage === 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === 1 ? 0.5 : 1,
                            fontSize: "12px"
                          }}
                        >
                          ← Anterior
                        </button>
                        <span style={{ padding: "4px 12px", minWidth: "40px", textAlign: "center" }}>{currentPage} / {totalPages}</span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: "4px 12px",
                            border: "1px solid rgba(255,255,255,0.2)",
                            background: "transparent",
                            color: "var(--text-primary)",
                            borderRadius: "4px",
                            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages ? 0.5 : 1,
                            fontSize: "12px"
                          }}
                        >
                          Próxima →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            )
          ) : tab === "myPayments" ? (
            <div className={styles.listSection}>
              {hasActivePlatformSubscription ? (
                <>
                  <p className={styles.sub} style={{ marginBottom: 16 }}>
                    Sua assinatura com a plataforma TreinoNexo.
                  </p>
                  <div className={styles.pricingGrid} style={{ maxWidth: 340 }}>
                    <div className={`${styles.pricingCard} ${lastSubscription?.unlocks_student_billing || lastOneOffAccess?.unlocks_student_billing ? styles.featured : ""}`}>
                      {(lastSubscription?.unlocks_student_billing || lastOneOffAccess?.unlocks_student_billing) && (
                        <span className={styles.pricingCardBadge}>Premium</span>
                      )}
                      <h4 className={styles.pricingCardName}>
                        {lastSubscription ? lastSubscription.plan_name : "Pagamento avulso"}
                      </h4>
                      <div className={styles.pricingCardPrice}>
                        R$ {parseFloat(lastSubscription ? lastSubscription.plan_amount : lastOneOffAccess?.amount ?? 0).toFixed(2)}
                      </div>
                      <div className={styles.pricingCardInterval}>
                        {lastSubscription
                          ? (lastSubscription.status === "canceled"
                            ? `Cancelada - acesso válido até ${formatDate(lastSubscription.current_period_end)}`
                            : `Próxima cobrança: ${formatDate(lastSubscription.current_period_end)}`)
                          : `Válido até ${formatDate(lastOneOffAccess?.expires_at)}`}
                      </div>
                      <ul className={styles.pricingCardFeatures}>
                        <li className={styles.pricingCardFeature}>
                          <FontAwesomeIcon icon={faCheck} style={{ color: "var(--accent)" }} />
                          Gestão de treinos e alunos
                        </li>
                        <li className={`${styles.pricingCardFeature} ${(lastSubscription?.unlocks_student_billing || lastOneOffAccess?.unlocks_student_billing) ? "" : styles.disabled}`}>
                          <FontAwesomeIcon
                            icon={(lastSubscription?.unlocks_student_billing || lastOneOffAccess?.unlocks_student_billing) ? faCheck : faXmark}
                            style={{ color: (lastSubscription?.unlocks_student_billing || lastOneOffAccess?.unlocks_student_billing) ? "var(--accent)" : "var(--text-0-30)" }}
                          />
                          Cobrança de mensalidade dos alunos
                        </li>
                      </ul>
                      <button className={styles.planBtn} onClick={() => handleOpenCheckout(null)}>
                        Gerenciar assinatura
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className={styles.sub} style={{ marginBottom: 16 }}>
                    Escolha um plano de assinatura para usar a plataforma TreinoNexo. Seu acesso depende de uma assinatura ativa.
                  </p>
                  {loadingPlans ? (
                    <div className={styles.loadingMsg}>Carregando planos…</div>
                  ) : plans.length === 0 ? (
                    <div className={styles.emptyMsg}>Nenhum plano disponível no momento.</div>
                  ) : (
                    <div className={styles.pricingGrid}>
                      {plans.map(plan => (
                        <div key={plan.id} className={`${styles.pricingCard} ${plan.unlocks_student_billing ? styles.featured : ""}`}>
                          {plan.unlocks_student_billing && <span className={styles.pricingCardBadge}>Premium</span>}
                          <h4 className={styles.pricingCardName}>{plan.name}</h4>
                          <div className={styles.pricingCardPrice}>
                            {plan.amount ? `R$ ${parseFloat(plan.amount).toFixed(2)}` : "Grátis"}
                          </div>
                          <div className={styles.pricingCardInterval}>
                            {PLAN_INTERVAL_LABELS[plan.interval] || plan.interval || ""}
                          </div>
                          <ul className={styles.pricingCardFeatures}>
                            <li className={styles.pricingCardFeature}>
                              <FontAwesomeIcon icon={faCheck} style={{ color: "var(--accent)" }} />
                              Gestão de treinos e alunos
                            </li>
                            <li className={`${styles.pricingCardFeature} ${plan.unlocks_student_billing ? "" : styles.disabled}`}>
                              <FontAwesomeIcon icon={plan.unlocks_student_billing ? faCheck : faXmark}
                                style={{ color: plan.unlocks_student_billing ? "var(--accent)" : "var(--text-0-30)" }} />
                              Cobrança de mensalidade dos alunos
                            </li>
                          </ul>
                          <button
                            className={styles.planBtn}
                            onClick={() => handleOpenCheckout(plan)}
                            title="Assinar este plano"
                            style={currentUser?.own_brand_color ? {
                              background: currentUser.own_brand_color
                            } : undefined}
                          >
                            Assinar
                          </button>
                          {plan.interval !== "monthly" && plan.amount ? (
                            <p style={{ fontStyle: "italic", fontSize: 12, color: "var(--text-0-50)", textAlign: "center", margin: "8px 0 0" }}>
                              equivale a R$ {(parseFloat(plan.amount) / (MONTHS_PER_PLAN_INTERVAL[plan.interval] || 1)).toFixed(2)}/mês
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : tab === "analytics" ? (
            !hasStudentBillingManagementAccess ? (
              <UpgradeRequiredNotice onGoToPlans={() => setTab("myPayments")} />
            ) : (
            <div className={styles.listSection}>
              {loadingAnalytics ? (
                <div className={styles.loadingMsg}>Carregando…</div>
              ) : analytics ? (
                <>
                  <div className={styles.kpiGroup}>
                    <h4 className={styles.kpiGroupTitle}>Receita</h4>
                    <div className={styles.kpiGrid}>
                      <div className={styles.analyticsCard}>
                        <div className={styles.analyticsCardLabel}>Receita Total</div>
                        <div className={`${styles.analyticsCardValue} ${styles.accent}`}>R$ {analytics.total_revenue.toFixed(2)}</div>
                      </div>
                      <div className={styles.analyticsCard}>
                        <div className={styles.analyticsCardLabel}>Receita Estimada Mês</div>
                        <div className={`${styles.analyticsCardValue} ${styles.accent}`}>R$ {analytics.estimated_monthly_revenue.toFixed(2)}</div>
                      </div>
                      <div className={`${styles.analyticsCard} ${styles.danger}`}>
                        <div className={styles.analyticsCardLabel}>Receita Pendente</div>
                        <div className={`${styles.analyticsCardValue} ${styles.danger}`}>R$ {analytics.pending_revenue.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.kpiGroup}>
                    <h4 className={styles.kpiGroupTitle}>Alunos</h4>
                    <div className={styles.kpiGrid}>
                      <div className={styles.analyticsCard}>
                        <div className={styles.analyticsCardLabel}>Alunos Pagantes</div>
                        <div className={styles.analyticsCardValue}>{analytics.students_paying} / {analytics.total_students}</div>
                        <div className={styles.analyticsCardSub}>{analytics.paying_percentage}% da base</div>
                      </div>
                      <div className={styles.analyticsCard}>
                        <div className={styles.analyticsCardLabel}>Próximos Vencimentos (7 dias)</div>
                        <div className={styles.analyticsCardValue}>{analytics.upcoming_expiring_count}</div>
                        <div className={styles.analyticsCardSub}>R$ {analytics.upcoming_expiring_revenue.toFixed(2)}</div>
                      </div>
                      <div className={styles.analyticsCard}>
                        <div className={styles.analyticsCardLabel}>Plano Mais Popular</div>
                        <div className={styles.analyticsCardValue} style={{ fontSize: "18px" }}>{analytics.popular_plan_name || "-"}</div>
                        <div className={styles.analyticsCardSub}>{analytics.popular_plan_count} alunos</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            )
          ) : tab === "receiving" ? (
            !hasStudentBillingManagementAccess ? (
              <UpgradeRequiredNotice onGoToPlans={() => setTab("myPayments")} />
            ) : (
            <div className={styles.listSection}>
              {loadingAsaas ? (
                <div className={styles.loadingMsg}>Carregando…</div>
              ) : asaasSubaccount?.status === "approved" ? (
                <div style={{ textAlign: "center", padding: "32px 20px" }}>
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 28, color: "var(--accent)", marginBottom: 12 }} />
                  <p style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 6 }}>
                    Recebimento direto ativo
                  </p>
                  <p style={{ color: "var(--text-0-50)", fontSize: 13, maxWidth: 420, margin: "0 auto" }}>
                    Os pagamentos dos seus alunos caem direto na sua própria conta Asaas - sem repasse manual.
                    Pra ver saldo e sacar, acesse sua conta pelo site ou app da Asaas (o mesmo cadastro que você ativou por e-mail).
                  </p>
                </div>
              ) : asaasSubaccount?.status === "pending" ? (
                <div style={{ textAlign: "center", padding: "32px 20px" }}>
                  <FontAwesomeIcon icon={faHourglassHalf} style={{ fontSize: 28, color: "var(--text-0-50)", marginBottom: 12 }} />
                  <p style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 6 }}>
                    Aguardando aprovação da Asaas
                  </p>
                  <p style={{ color: "var(--text-0-50)", fontSize: 13, maxWidth: 420, margin: "0 auto" }}>
                    Você recebeu um e-mail da Asaas pra ativar a conta e enviar seus documentos (identificação + selfie).
                    A análise costuma levar até 48h. Assim que for aprovado, os pagamentos dos seus alunos passam a cair
                    direto na sua conta automaticamente - nada muda até lá.
                  </p>
                  <button className={styles.planBtn} onClick={loadAsaasSubaccount} style={{ marginTop: 12 }}>
                    <FontAwesomeIcon icon={faRotateRight} /> Verificar status
                  </button>
                </div>
              ) : asaasSubaccount?.status === "rejected" ? (
                <div style={{ textAlign: "center", padding: "32px 20px" }}>
                  <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: 28, color: "var(--danger, #e05252)", marginBottom: 12 }} />
                  <p style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 6 }}>
                    Cadastro não aprovado pela Asaas
                  </p>
                  <p style={{ color: "var(--text-0-50)", fontSize: 13, maxWidth: 420, margin: "0 auto" }}>
                    Enquanto isso, seus alunos continuam pagando normalmente (o dinheiro cai na conta do TreinoNexo,
                    com repasse combinado por fora). Fale com o suporte pra entender o motivo e tentar de novo.
                  </p>
                </div>
              ) : (
                <>
                  <p className={styles.sub} style={{ marginBottom: 4 }}>
                    Receba o pagamento dos seus alunos direto na sua própria conta Asaas - sem repasse manual.
                  </p>
                  <p className={styles.sub} style={{ marginBottom: 16, fontSize: 12 }}>
                    Precisa de CNPJ (MEI serve). Depois de criar, você recebe um e-mail da Asaas pra ativar a conta
                    e enviar seus documentos - isso acontece fora do TreinoNexo, direto com a Asaas.
                  </p>
                  {asaasError && <div className={styles.error}>{asaasError}</div>}
                  <form
                    onSubmit={handleSubmitAsaasSubaccount}
                    style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 560 }}
                  >
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      CEP
                      <input
                        required
                        value={asaasForm.postal_code}
                        onChange={e => handleCepChange(e.target.value)}
                        placeholder="00000-000"
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <div style={{ display: "flex", alignItems: "flex-end", fontSize: 12, color: "var(--text-0-50)" }}>
                      {cepStatus === "loading" && "Buscando endereço..."}
                      {cepStatus === "found" && "Endereço encontrado - confira e complete o número."}
                      {cepStatus === "not_found" && "CEP não encontrado - preencha o endereço manualmente."}
                      {cepStatus === "error" && "Não deu pra buscar o CEP agora - preencha o endereço manualmente."}
                    </div>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)", gridColumn: "1 / -1" }}>
                      Endereço
                      <input
                        required
                        readOnly={cepStatus === "found"}
                        value={asaasForm.address}
                        onChange={e => handleAsaasFormChange("address", e.target.value)}
                        style={cepStatus === "found" ? { padding: "8px 10px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, background: "rgba(255,255,255,0.03)", color: "var(--text-0-70)", fontSize: 13 } : { padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Número
                      <input
                        required
                        value={asaasForm.address_number}
                        onChange={e => handleAsaasFormChange("address_number", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Complemento (opcional)
                      <input
                        value={asaasForm.complement}
                        onChange={e => handleAsaasFormChange("complement", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Bairro
                      <input
                        required
                        readOnly={cepStatus === "found"}
                        value={asaasForm.province}
                        onChange={e => handleAsaasFormChange("province", e.target.value)}
                        style={cepStatus === "found" ? { padding: "8px 10px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, background: "rgba(255,255,255,0.03)", color: "var(--text-0-70)", fontSize: 13 } : { padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      CNPJ
                      <input
                        required
                        value={asaasForm.cpf_cnpj}
                        onChange={e => handleAsaasFormChange("cpf_cnpj", e.target.value)}
                        placeholder="00.000.000/0000-00"
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Tipo de empresa
                      <select
                        required
                        value={asaasForm.company_type}
                        onChange={e => handleAsaasFormChange("company_type", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      >
                        <option value="MEI">MEI</option>
                        <option value="LIMITED">Limitada (LTDA)</option>
                        <option value="INDIVIDUAL">Empresário individual</option>
                        <option value="ASSOCIATION">Associação</option>
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Data de nascimento do responsável
                      <SplitDateInput
                        value={asaasForm.birth_date}
                        onChange={v => handleAsaasFormChange("birth_date", v)}
                        maxDate={todayLocalISO()}
                        inputStyle={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Telefone fixo
                      <input
                        required
                        value={asaasForm.phone}
                        onChange={e => handleAsaasFormChange("phone", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Celular
                      <input
                        required
                        value={asaasForm.mobile_phone}
                        onChange={e => handleAsaasFormChange("mobile_phone", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--text-0-70)" }}>
                      Faturamento mensal estimado (R$, opcional)
                      <input
                        type="number" step="0.01"
                        value={asaasForm.income_value}
                        onChange={e => handleAsaasFormChange("income_value", e.target.value)}
                        style={{ padding: "8px 10px", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, background: "var(--border-0-05)", color: "var(--text-primary)", fontSize: 13 }}
                      />
                    </label>
                    <button
                      type="submit"
                      className={styles.planBtn}
                      disabled={submittingAsaas}
                      style={{ gridColumn: "1 / -1", marginTop: 8 }}
                    >
                      {submittingAsaas ? "Enviando…" : "Criar minha conta de recebimento"}
                    </button>
                  </form>
                </>
              )}
            </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
